import { useMemo, useRef, useState } from "react";
import { X, Plus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { authedFileUrl } from "./PhotoUploader";

/**
 * Damage marker canvas.  Renders 5 SVG vehicle silhouettes and lets the user
 * tap anywhere on a silhouette to drop a damage marker.  Each marker stores
 * relative x/y (0..1) so it re-renders correctly at any size, plus a type,
 * severity, note and optional photo id.
 *
 * Props:
 *   markers     — Array<Marker>  { id, view, x, y, type, severity, note, photo_url }
 *   onChange    — (nextMarkers) => void
 *   refId       — job card id (required for photo upload)
 *   readOnly    — bool
 */

const VIEWS = [
  { key: "front",    label: "Front" },
  { key: "left",     label: "Left" },
  { key: "top",      label: "Top" },
  { key: "right",    label: "Right" },
  { key: "rear",     label: "Rear" },
];

const TYPES = [
  { key: "scratch",       label: "Scratch",       color: "#F79009" },
  { key: "dent",          label: "Dent",          color: "#e63946" },
  { key: "broken",        label: "Broken",        color: "#7C1D3B" },
  { key: "cracked",       label: "Cracked",       color: "#c94a6a" },
  { key: "missing",       label: "Missing",       color: "#475467" },
  { key: "paint_damage",  label: "Paint damage",  color: "#457b9d" },
  { key: "other",         label: "Other",         color: "#98A2B3" },
];

const SEVERITIES = [
  { key: "minor",    label: "Minor" },
  { key: "moderate", label: "Moderate" },
  { key: "severe",   label: "Severe" },
];

export default function DamageMarker({ markers = [], onChange, refId, readOnly = false }) {
  const [activeView, setActiveView] = useState("front");
  const [editing, setEditing] = useState(null); // marker id being edited
  const surfaceRef = useRef(null);

  const filtered = useMemo(() => markers.filter((m) => m.view === activeView), [markers, activeView]);

  const dropMarker = (e) => {
    if (readOnly) return;
    const rect = surfaceRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    const m = {
      id: Math.random().toString(36).slice(2),
      view: activeView, x, y,
      type: "scratch", severity: "minor",
      note: "", photo_url: "",
    };
    onChange?.([...markers, m]);
    setEditing(m.id);
  };

  const updateMarker = (id, patch) => {
    onChange?.(markers.map((m) => m.id === id ? { ...m, ...patch } : m));
  };
  const removeMarker = (id) => {
    onChange?.(markers.filter((m) => m.id !== id));
    if (editing === id) setEditing(null);
  };

  const attachPhoto = async (id, file) => {
    if (!refId) { toast.info("Save the job card first to attach photos."); return; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, {
        params: { context: "damage", ref_id: refId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateMarker(id, { photo_url: r.data.url, photo_id: r.data.id });
      toast.success("Photo attached");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  const active = editing ? markers.find((m) => m.id === editing) : null;
  const activeType = active ? (TYPES.find((t) => t.key === active.type) || TYPES[0]) : TYPES[0];

  return (
    <div className="space-y-3" data-testid="jc-damage-marker">
      {/* View selector */}
      <div className="flex flex-wrap gap-1 bg-white rounded-lg border border-[#E4E7EC] p-1">
        {VIEWS.map((v) => {
          const cnt = markers.filter((m) => m.view === v.key).length;
          return (
            <button
              key={v.key}
              type="button"
              onClick={() => setActiveView(v.key)}
              data-testid={`jc-damage-view-${v.key}`}
              className={`h-8 px-3 rounded-md text-[11.5px] font-semibold inline-flex items-center gap-1.5 transition-colors ${
                activeView === v.key ? "bg-[#1d3557] text-white" : "text-[#667085] hover:text-[#1d3557]"
              }`}
            >
              {v.label}
              {cnt > 0 && (
                <span className={`text-[10px] tabular-nums rounded-full px-1.5 ${activeView === v.key ? "bg-white/20" : "bg-[#e63946]/12 text-[#e63946]"}`}>
                  {cnt}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Canvas */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <div
            ref={surfaceRef}
            onClick={dropMarker}
            data-testid="jc-damage-surface"
            className={`relative rounded-xl border border-[#E4E7EC] bg-[#F7F9FC] overflow-hidden select-none aspect-[16/10] ${readOnly ? "" : "cursor-crosshair"}`}
          >
            <VehicleSilhouette view={activeView} />
            {filtered.map((m) => {
              const t = TYPES.find((x) => x.key === m.type) || TYPES[0];
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setEditing(m.id); }}
                  data-testid={`jc-damage-marker-${m.id}`}
                  style={{
                    left: `${m.x * 100}%`, top: `${m.y * 100}%`,
                    background: t.color,
                    boxShadow: editing === m.id ? "0 0 0 3px rgba(230, 57, 70, 0.35)" : "0 2px 5px rgba(29,53,87,0.25)",
                  }}
                  className="absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full grid place-items-center text-white text-[9px] font-bold border-2 border-white hover:scale-110 transition-transform"
                >
                  {t.label[0]}
                </button>
              );
            })}
            {!readOnly && filtered.length === 0 && (
              <div className="absolute inset-0 grid place-items-center text-[11.5px] text-[#667085] pointer-events-none">
                Tap anywhere on the vehicle to drop a marker.
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="mt-2 flex flex-wrap gap-2 text-[10.5px] text-[#667085]">
            {TYPES.map((t) => (
              <span key={t.key} className="inline-flex items-center gap-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
                {t.label}
              </span>
            ))}
          </div>
        </div>

        {/* Editor / list */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-[#E4E7EC] bg-white p-3">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-[#667085] mb-2">
              Markers on this view ({filtered.length})
            </div>
            {filtered.length === 0 ? (
              <div className="text-[11.5px] text-[#667085] italic py-4 text-center">No damage recorded on this view.</div>
            ) : (
              <ul className="space-y-2 max-h-72 overflow-auto pr-1">
                {filtered.map((m) => {
                  const t = TYPES.find((x) => x.key === m.type) || TYPES[0];
                  return (
                    <li
                      key={m.id}
                      onClick={() => setEditing(m.id)}
                      className={`rounded-lg border p-2.5 cursor-pointer ${editing === m.id ? "border-[#e63946] bg-[#e63946]/5" : "border-[#E4E7EC] hover:bg-[#F7F9FC]"}`}
                    >
                      <div className="flex items-center gap-2 text-[12px]">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
                        <span className="font-semibold text-[#1d3557]">{t.label}</span>
                        <span className="text-[#667085] text-[10.5px] uppercase tracking-wider">{m.severity}</span>
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeMarker(m.id); }}
                            className="ml-auto text-[#a8dadc] hover:text-[#e63946]"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                      {m.note && <div className="text-[11px] text-[#667085] mt-1">{m.note}</div>}
                      {m.photo_url && (
                        <img src={authedFileUrl(m.photo_url)} alt="" className="mt-2 w-full h-20 object-cover rounded" />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Editor for active marker */}
          {active && !readOnly && (
            <div className="mt-2 rounded-xl border border-[#e63946]/30 bg-[#e63946]/5 p-3 space-y-2" data-testid="jc-damage-editor">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-3 h-3 rounded-full" style={{ background: activeType.color }} />
                <div className="text-[12px] font-bold text-[#1d3557]">Edit marker</div>
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="ml-auto text-[#667085] hover:text-[#e63946]"
                >
                  <X size={13} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={active.type}
                  onChange={(e) => updateMarker(active.id, { type: e.target.value })}
                  data-testid="jc-damage-type"
                  className="h-9 px-2 rounded-md border border-[#E4E7EC] text-[12px] bg-white focus:border-[#457b9d] outline-none"
                >
                  {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                <select
                  value={active.severity}
                  onChange={(e) => updateMarker(active.id, { severity: e.target.value })}
                  data-testid="jc-damage-severity"
                  className="h-9 px-2 rounded-md border border-[#E4E7EC] text-[12px] bg-white focus:border-[#457b9d] outline-none"
                >
                  {SEVERITIES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <textarea
                rows={2}
                value={active.note}
                onChange={(e) => updateMarker(active.id, { note: e.target.value })}
                data-testid="jc-damage-note"
                placeholder="Note (visible position, size, root cause…)"
                className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[12px] bg-white focus:border-[#457b9d] outline-none"
              />
              <label className="block">
                <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Photo</span>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => e.target.files?.[0] && attachPhoto(active.id, e.target.files[0])}
                    data-testid="jc-damage-photo"
                    className="text-[11px]"
                  />
                  {active.photo_url && (
                    <a href={authedFileUrl(active.photo_url)} target="_blank" rel="noreferrer" className="text-[11px] text-[#457b9d] underline">
                      preview
                    </a>
                  )}
                </div>
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------- Vehicle sketches ---------------
 * Line-art PNG sketches served from /car-sketches/{view}.png.
 * The click-to-drop-marker behaviour uses the surrounding container's
 * bounding box, so the marker positions remain resolution-independent
 * (stored as 0..1 relative coords). */

const SKETCH_SRC = {
  front: "/car-sketches/front.png",
  rear:  "/car-sketches/rear.png",
  left:  "/car-sketches/left.png",
  right: "/car-sketches/right.png",
  top:   "/car-sketches/top.png",
};

function VehicleSilhouette({ view }) {
  const src = SKETCH_SRC[view] || SKETCH_SRC.front;
  return (
    <img
      src={src}
      alt={`${view} view`}
      draggable={false}
      data-testid={`jc-damage-sketch-${view}`}
      className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
    />
  );
}
